import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../assets/NotificationList.css';
import '../assets/Pagination.css';

// Use production URL if on production domain, otherwise use env var or localhost
const getApiBaseUrl = () => {
  if (window.location.hostname === 'portal.jjtutorialsg.com') {
    return 'https://jjt-tutor-portal.onrender.com/api';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

const NotificationList = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [sortOrder, setSortOrder] = useState('newest'); // newest, oldest
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    fetchNotifications();
  }, [filter, sortOrder, currentPage]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });

      if (filter === 'unread') {
        params.append('unreadOnly', 'true');
      }

      const response = await axios.get(
        `${API_BASE_URL}/notifications?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let fetchedNotifications = response.data.data.notifications;
      const total = response.data.data.total;

      // Filter by read status if needed (client-side for 'read' filter)
      if (filter === 'read') {
        fetchedNotifications = fetchedNotifications.filter(n => n.isRead);
      }

      // Sort notifications
      if (sortOrder === 'oldest') {
        fetchedNotifications = [...fetchedNotifications].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      } else {
        // newest is default from backend, but ensure it
        fetchedNotifications = [...fetchedNotifications].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      }

      setNotifications(fetchedNotifications);
      setTotalCount(total);
      setTotalPages(Math.ceil(total / ITEMS_PER_PAGE));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }

    // Navigate to link if provided
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const markAsRead = async (notificationId) => {
    const previousNotifications = [...notifications];

    // Update UI optimistically
    setNotifications(prev =>
      prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
    );

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setNotifications(previousNotifications);
      alert('Failed to mark notification as read. Please try again.');
    }
  };

  const markAsUnread = async (notificationId, event) => {
    event.stopPropagation();
    const previousNotifications = [...notifications];

    // Update UI optimistically
    setNotifications(prev =>
      prev.map(n => n._id === notificationId ? { ...n, isRead: false } : n)
    );

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/notifications/${notificationId}/unread`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      setNotifications(previousNotifications);
      alert('Failed to mark notification as unread. Please try again.');
    }
  };

  const deleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    
    const previousNotifications = [...notifications];

    // Update UI optimistically
    setNotifications(prev => prev.filter(n => n._id !== notificationId));
    setTotalCount(prev => prev - 1);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
      setNotifications(previousNotifications);
      setTotalCount(prev => prev + 1);
      alert('Failed to delete notification. Please try again.');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.isRead);
    
    if (unreadNotifications.length === 0) {
      showToast('No unread notifications to mark as read', 'info');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      for (const notification of unreadNotifications) {
        await axios.put(
          `${API_BASE_URL}/notifications/${notification._id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Refresh notifications
      await fetchNotifications();
      showToast(`${unreadNotifications.length} notification(s) marked as read`, 'success');
    } catch (error) {
      console.error('Error marking all as read:', error);
      showToast('Failed to mark all as read. Please try again.', 'error');
    }
  };

  const clearAllRead = async () => {
    const readNotifications = notifications.filter(n => n.isRead);
    
    if (readNotifications.length === 0) {
      showToast('No read notifications to clear', 'info');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${readNotifications.length} read notification(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_BASE_URL}/notifications/clear-read`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const count = response.data.data?.count || readNotifications.length;

      // Refresh notifications
      await fetchNotifications();
      showToast(`${count} notification(s) cleared successfully`, 'success');
    } catch (error) {
      console.error('Error clearing read notifications:', error);
      showToast('Failed to clear read notifications. Please try again.', 'error');
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      'RATE_UPDATE': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      'NEW_TUTEE': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      'LESSON_SUBMISSION': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      'SUBJECT_RATE_UPDATE': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      'ENDORSEMENT_REMINDER': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      'ENDORSEMENT_COMPLETED': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 4 12 14.01l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      'LESSON_DISPUTED': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      'DISPUTE_RESOLVED': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      'INVOICE_GENERATED': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      'INVOICE_EMAILED': (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="m22 6-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    };
    
    return iconMap[type] || (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatFullDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRetry = () => {
    fetchNotifications();
  };

  return (
    <div className="notification-list-page">
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">
            {toast.type === 'success' && '✓'}
            {toast.type === 'error' && '✕'}
            {toast.type === 'info' && 'ℹ'}
          </span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}
      
      <div className="notification-list-container">
        <div className="notification-list-header">
          <h1>Notifications</h1>
          <p className="notification-count">
            {totalCount} {totalCount === 1 ? 'notification' : 'notifications'}
          </p>
        </div>

        <div className="notification-controls">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => {
                setFilter('all');
                setCurrentPage(1);
              }}
            >
              All
            </button>
            <button
              className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => {
                setFilter('unread');
                setCurrentPage(1);
              }}
            >
              Unread
            </button>
            <button
              className={`filter-tab ${filter === 'read' ? 'active' : ''}`}
              onClick={() => {
                setFilter('read');
                setCurrentPage(1);
              }}
            >
              Read
            </button>
          </div>

          <div className="control-actions">
            <div className="sort-dropdown">
              <label htmlFor="sort-select">Sort by:</label>
              <select
                id="sort-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>

            {notifications.length > 0 && (
              <div className="notification-action-buttons">
                <button className="notif-action-btn mark-read-btn" onClick={markAllAsRead}>
                  Mark All as Read
                </button>
                <button className="notif-action-btn clear-read-btn" onClick={clearAllRead}>
                  Clear All Read
                </button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="notification-error">
            <p>{error}</p>
            <button className="retry-btn" onClick={handleRetry}>
              Retry
            </button>
          </div>
        )}

        {loading ? (
          <div className="notification-loading">
            <div className="loading-spinner"></div>
            <span>Loading notifications...</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notification-empty">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>No notifications</h2>
            <p>
              {filter === 'unread' && 'All caught up! You have no unread notifications.'}
              {filter === 'read' && 'You have no read notifications.'}
              {filter === 'all' && 'You have no notifications yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="notifications-grid">
              {notifications.map(notification => (
                <div
                  key={notification._id}
                  className={`notification-card ${!notification.isRead ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-card-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-card-content">
                    <div className="notification-card-header">
                      <h3 className="notification-card-title">{notification.title}</h3>
                      {!notification.isRead && <span className="unread-indicator">●</span>}
                    </div>
                    <p className="notification-card-message">{notification.message}</p>
                    <div className="notification-card-meta">
                      <span className="notification-card-time">
                        {formatFullDateTime(notification.createdAt)}
                      </span>
                      {notification.isRead && notification.readAt && (
                        <>
                          <span className="meta-divider">•</span>
                          <span className="notification-read-time">
                            Read: {formatFullDateTime(notification.readAt)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="notif-card-actions">
                    <button
                      className={`notif-icon-btn ${notification.isRead ? 'mark-unread' : 'mark-read'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        notification.isRead ? markAsUnread(notification._id, e) : markAsRead(notification._id);
                      }}
                      title={notification.isRead ? 'Mark as unread' : 'Mark as read'}
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 9s2.5-5 8-5 8 5 8 5-2.5 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      </svg>
                    </button>
                    <button
                      className="notif-icon-btn delete-notif"
                      onClick={(e) => deleteNotification(notification._id, e)}
                      title="Delete"
                    >
                      <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4h14M5 1h6M5 8v6M11 8v6M2 4l1 11c0 .6.4 1 1 1h8c.6 0 1-.4 1-1l1-11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>
                <div className="pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationList;
